/**
 * Personas — who a case runs as. `owner` is the application's owner; every
 * other persona is a temporary project user holding the listed roles.
 */

import { useState } from 'react'
import toast from 'react-hot-toast'
import { Plus, Save, X } from 'lucide-react'
import Button from '@/components/common/Button'
import Modal from '@/components/common/Modal'
import { apiError, savePlan, type TestPlan } from '@/api/uiTesting'
import { inputStyle, mutedText, tableStyle, tdStyle, thStyle } from './shared'

export default function PlanEditor({ project, plan, canTest, onClose, onSaved }: {
  project: string; plan: TestPlan | null; canTest: boolean
  onClose: () => void; onSaved: () => Promise<void> | void
}) {
  const initial = Object.entries(plan?.personas || { owner: { roles: [] } })
    .map(([name, p]) => ({ name, roles: (p?.roles || []).join(', ') }))
  const [rows, setRows] = useState(initial.length ? initial : [{ name: 'owner', roles: '' }])
  const [busy, setBusy] = useState(false)

  const onSave = async () => {
    const personas: Record<string, { roles: string[] }> = {}
    for (const r of rows) {
      const name = r.name.trim().toLowerCase()
      if (!name) continue
      const roles = r.roles.split(',').map((x) => x.trim().toLowerCase()).filter(Boolean)
      if (name !== 'owner' && !roles.length) {
        toast.error(`Persona "${name}" needs at least one role`)
        return
      }
      personas[name] = { roles: name === 'owner' ? [] : roles }
    }
    if (!personas.owner) personas.owner = { roles: [] }
    setBusy(true)
    try {
      const { error: _ignored, ...rest } = plan || ({ personas } as TestPlan)
      void _ignored
      await savePlan(project, { ...rest, personas } as TestPlan)
      toast.success('Personas saved')
      await onSaved()
      onClose()
    } catch (err) {
      toast.error(apiError(err, 'Could not save the personas'))
    } finally {
      setBusy(false)
    }
  }

  return (
    <Modal open onClose={onClose} title="Personas" size="wide" footer={
      <>
        <Button variant="secondary" onClick={onClose}>Close</Button>
        {canTest && <Button icon={<Save size={14} />} loading={busy} onClick={onSave}>Save</Button>}
      </>
    }>
      <div style={{ ...mutedText, marginBottom: 10 }}>
        A case runs as one persona. <b>owner</b> is the application&apos;s owner. Every other persona runs
        as a temporary project user holding the roles listed here, created for the run and removed
        afterwards — so the application&apos;s own grants decide what it sees.
      </div>
      <table style={tableStyle}>
        <thead><tr><th style={thStyle}>Persona</th><th style={thStyle}>Functional roles (comma-separated)</th><th style={thStyle} /></tr></thead>
        <tbody>
          {rows.map((r, i) => (
            <tr key={i}>
              <td style={tdStyle}>
                <input value={r.name} disabled={!canTest || r.name === 'owner'} style={{ ...inputStyle, width: '100%' }}
                       onChange={(e) => setRows(rows.map((x, j) => (j === i ? { ...x, name: e.target.value } : x)))} />
              </td>
              <td style={tdStyle}>
                <input value={r.name === 'owner' ? '(the owner — no roles)' : r.roles}
                       disabled={!canTest || r.name === 'owner'} style={{ ...inputStyle, width: '100%' }}
                       onChange={(e) => setRows(rows.map((x, j) => (j === i ? { ...x, roles: e.target.value } : x)))} />
              </td>
              <td style={tdStyle}>
                {canTest && r.name !== 'owner' && (
                  <button className="btn btn-ghost btn-sm" aria-label="Remove persona"
                          onClick={() => setRows(rows.filter((_, j) => j !== i))}><X size={14} /></button>
                )}
              </td>
            </tr>
          ))}
        </tbody>
      </table>
      {canTest && (
        <Button variant="ghost" size="sm" icon={<Plus size={14} />} style={{ marginTop: 8 }}
                onClick={() => setRows([...rows, { name: '', roles: '' }])}>Add persona</Button>
      )}
    </Modal>
  )
}
